import { useEffect, useMemo, useState, type FormEvent } from 'react';
import Modal from '@/components/base/Modal';
import Button from '@/components/base/Button';
import Input from '@/components/base/Input';
import Select from '@/components/base/Select';
import Textarea from '@/components/base/Textarea';
import { useToast } from '@/components/feature/ToastProvider';
import { useAuth } from '@/contexts/AuthContext';
import { saveMovement } from '@/lib/financeService';
import { getCategoriesByType } from '@/lib/categories';
import { todayISO } from '@/lib/format';
import type { FinancialMovement, MovementType, Project } from '@/types';

interface MovementFormProps {
  open: boolean;
  movement?: FinancialMovement | null;
  projects: Project[];
  onClose: () => void;
  onSaved: () => void;
}

const TYPE_OPTIONS = [
  { value: 'INGRESO', label: 'Ingreso' },
  { value: 'EGRESO', label: 'Egreso' },
];

export default function MovementForm({ open, movement, projects, onClose, onSaved }: MovementFormProps) {
  const { notify } = useToast();
  const { user, profile } = useAuth();

  const [type, setType] = useState<MovementType>('INGRESO');
  const [date, setDate] = useState(todayISO());
  const [category, setCategory] = useState('');
  const [concept, setConcept] = useState('');
  const [amount, setAmount] = useState('');
  const [projectId, setProjectId] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  const isEdit = Boolean(movement);

  useEffect(() => {
    if (!open) return;
    if (movement) {
      setType(movement.movement_type);
      setDate(movement.movement_date);
      setCategory(movement.category);
      setConcept(movement.concept);
      setAmount(String(movement.amount));
      setProjectId(movement.project_id ?? '');
    } else {
      setType('INGRESO');
      setDate(todayISO());
      setCategory(getCategoriesByType('INGRESO')[0]);
      setConcept('');
      setAmount('');
      setProjectId('');
    }
    setErrors({});
    setSaving(false);
  }, [open, movement]);

  const categories = useMemo(() => getCategoriesByType(type), [type]);

  const handleTypeChange = (nextType: MovementType) => {
    setType(nextType);
    setCategory(getCategoriesByType(nextType)[0]);
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const next: Record<string, string> = {};
    if (!date) next.date = 'La fecha es obligatoria.';
    if (!category) next.category = 'Seleccione una categoría.';
    if (!concept.trim()) next.concept = 'El concepto es obligatorio.';
    const parsedAmount = Number(amount.replace(',', '.'));
    if (!amount.trim()) next.amount = 'El monto es obligatorio.';
    else if (Number.isNaN(parsedAmount) || parsedAmount <= 0) next.amount = 'El monto debe ser mayor a cero.';

    if (Object.keys(next).length > 0) {
      setErrors(next);
      notify({ tone: 'error', title: 'Revise la información', message: 'Complete los campos obligatorios.' });
      return;
    }

    setSaving(true);
    try {
      await saveMovement({
        id: movement?.id,
        movement_type: type,
        movement_date: date,
        category,
        concept,
        amount: parsedAmount,
        project_id: projectId || null,
        createdBy: user?.id ?? null,
        createdByName: profile?.full_name || profile?.username || 'Usuario',
      });
      notify({
        tone: 'success',
        title: isEdit ? 'Movimiento actualizado' : 'Movimiento registrado',
        message: 'La información financiera fue guardada correctamente.',
      });
      onSaved();
    } catch {
      notify({
        tone: 'error',
        title: 'No se pudo guardar',
        message: 'Ocurrió un problema al guardar el movimiento. Intente nuevamente.',
      });
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <Modal
      open={open}
      title={isEdit ? 'Editar movimiento' : 'Nuevo movimiento'}
      description="Registre ingresos y egresos para mantener el balance actualizado."
      icon="ri-exchange-dollar-line"
      size="md"
      onClose={onClose}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button type="submit" form="movement-form" icon="ri-save-line" loading={saving}>
            {isEdit ? 'Guardar cambios' : 'Registrar movimiento'}
          </Button>
        </>
      }
    >
      <form id="movement-form" onSubmit={handleSubmit} className="flex flex-col gap-4" noValidate>
        <div>
          <span className="mb-1.5 block text-xs font-semibold text-foreground-700 font-label">
            * Tipo de movimiento
          </span>
          <div className="flex flex-wrap items-center gap-1 rounded-full border border-background-200 bg-background-50 px-1 py-1">
            {TYPE_OPTIONS.map((option) => {
              const active = type === option.value;
              const isIncome = option.value === 'INGRESO';
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleTypeChange(option.value as MovementType)}
                  className={[
                    'flex flex-1 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors',
                    active
                      ? isIncome
                        ? 'bg-primary-500 text-background-50 dark:text-foreground-950'
                        : 'bg-red-600 text-white'
                      : 'text-foreground-600 hover:bg-background-100',
                  ].join(' ')}
                >
                  <i className={`${isIncome ? 'ri-arrow-down-circle-line' : 'ri-arrow-up-circle-line'} text-base`}></i>
                  {option.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Input
            label="* Fecha"
            name="date"
            type="date"
            value={date}
            onChange={(event) => {
              setDate(event.target.value);
              setErrors((prev) => ({ ...prev, date: '' }));
            }}
            error={errors.date}
          />
          <Select
            label="* Categoría"
            name="category"
            value={category}
            onChange={(event) => {
              setCategory(event.target.value);
              setErrors((prev) => ({ ...prev, category: '' }));
            }}
            options={categories.map((item) => ({ value: item, label: item }))}
            error={errors.category}
          />
        </div>

        <Textarea
          label="* Concepto"
          name="concept"
          rows={2}
          maxLength={500}
          showCounter
          placeholder="Descripción del movimiento..."
          value={concept}
          onChange={(event) => {
            setConcept(event.target.value);
            setErrors((prev) => ({ ...prev, concept: '' }));
          }}
          error={errors.concept}
        />

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Input
            label="* Monto (Bs.)"
            name="amount"
            inputMode="decimal"
            placeholder="0.00"
            value={amount}
            onChange={(event) => {
              setAmount(event.target.value);
              setErrors((prev) => ({ ...prev, amount: '' }));
            }}
            error={errors.amount}
          />
          <Select
            label="Proyecto relacionado (opcional)"
            name="projectId"
            placeholder="Sin proyecto asociado"
            value={projectId}
            onChange={(event) => setProjectId(event.target.value)}
            options={projects.map((project) => ({ value: project.id, label: project.name }))}
          />
        </div>
      </form>
    </Modal>
  );
}